import {
  HttpClient,
  HttpContextToken,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { AuthStore } from '../stores/auth.store';
import { manageLogoutInFlight } from './logout-gate.interceptor';
import {
  isRefreshableRequest,
  isUnauthorized,
  refreshSession,
} from './session-request';

export const SESSION_REFRESH_RETRIED = new HttpContextToken(() => false);

/**
 * Recovers a 401 from a session-authenticated request through one shared refresh.
 *
 * Concurrent 401s share one in-flight POST. The original request is retried
 * once. A failed refresh marks the session unauthenticated and is rethrown
 * to the caller. A second 401 on the retry is rethrown without another
 * refresh. Does not run while logout is in flight, and never refreshes a 401
 * from `/session/refresh` or sign-out. Sign-in uses the same refresh-and-retry
 * path so app bootstrap can recover an expired access token.
 */
export const SessionRefreshInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const http = inject(HttpClient);
  const authStore = inject(AuthStore);

  if (!isRefreshableRequest(req) || req.context.get(SESSION_REFRESH_RETRIED)) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!isUnauthorized(error) || manageLogoutInFlight()) {
        return throwError(() => error);
      }

      return refreshSession(http).pipe(
        catchError(() => {
          authStore.unauthenticate();
          return throwError(() => error);
        }),
        switchMap(() =>
          next(
            req.clone({
              context: req.context.set(SESSION_REFRESH_RETRIED, true),
            }),
          ).pipe(catchError((retryError: unknown) => throwError(() => retryError))),
        ),
      );
    }),
  );
};
